import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  VStack,
  Text,
} from '@chakra-ui/react';
import { scheduleExam } from 'services/exam.service';
import { getAllSubjects } from 'services/api';

/*
  Modal "Nuevo examen" (Módulo 7 - Requerimiento 7):

  Cliente pidió que NO se gestione un "nuevo período de examen", sino
  que el examen se cree individualmente por materia. En este modal
  pedimos:

    - Materia (obligatoria)
    - Fecha del examen (obligatoria)
    - Hora del examen (obligatoria)
    - Aula / ubicación (opcional)
    - Capacidad máxima (opcional, default null = sin tope)

  Ya no se pide `examPeriodId`: el backend lo resuelve solo tomando (o
  creando) un exam_period AUTO para el año de la fecha elegida.
*/
export default function ScheduleExamModal({ isOpen, onClose, onSuccess }) {
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    subjectId: '',
    examDate: '',
    examTime: '',
    location: '',
    maxStudents: '',
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) loadSubjects();
  }, [isOpen]);

  const loadSubjects = async () => {
    try {
      const response = await getAllSubjects();
      if (response.status === 200) {
        const all = response.payload.subjects || [];
        const enabledOnly = all.filter(
          (s) => s.enabled === true || s.enabled === 1 || s.enabled === undefined,
        );
        setSubjects(enabledOnly);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al cargar asignaturas:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const reset = () =>
    setFormData({
      subjectId: '',
      examDate: '',
      examTime: '',
      location: '',
      maxStudents: '',
    });

  const handleSubmit = async () => {
    if (!formData.subjectId || !formData.examDate || !formData.examTime) {
      toast({
        title: 'Faltan datos',
        description: 'Materia, fecha y hora son obligatorios',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        subjectId: Number(formData.subjectId),
        examDate: formData.examDate,
        examTime: formData.examTime,
        location: formData.location || undefined,
        maxStudents: formData.maxStudents
          ? Number(formData.maxStudents)
          : undefined,
      };
      const response = await scheduleExam(payload);
      if (response.status === 201 || response.status === 200) {
        toast({
          title: 'Examen creado',
          description: 'La inscripción ya está habilitada',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        reset();
        onSuccess?.();
        onClose?.();
      }
    } catch (error) {
      toast({
        title: 'No se pudo crear el examen',
        description:
          error?.message ||
          error?.response?.data?.message ||
          'Revise los datos ingresados',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Nuevo examen</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text color="gray.500" fontSize="sm">
              El examen se crea directamente para la materia elegida. El
              período se resuelve automáticamente por año.
            </Text>

            <FormControl isRequired>
              <FormLabel>Materia</FormLabel>
              <Select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
                placeholder="Seleccione una materia"
              >
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.description || subject.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Fecha del examen</FormLabel>
              <Input
                name="examDate"
                type="date"
                value={formData.examDate}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Hora del examen</FormLabel>
              <Input
                name="examTime"
                type="time"
                value={formData.examTime}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Aula / ubicación</FormLabel>
              <Input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="ej: Aula 101"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Capacidad máxima (opcional)</FormLabel>
              <Input
                name="maxStudents"
                type="number"
                min="1"
                value={formData.maxStudents}
                onChange={handleChange}
                placeholder="Dejar vacío para sin tope"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancelar
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Crear examen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
